import { useEffect } from 'react'

interface SEOHeadProps {
  title: string
  description: string
  keywords?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  canonical?: string
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  canonical
}) => {
  useEffect(() => {
    // Update page title
    document.title = title

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', description)
    } else {
      const meta = document.createElement('meta')
      meta.name = 'description'
      meta.content = description
      document.head.appendChild(meta)
    }

    // Update meta keywords
    if (keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]')
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords)
      } else {
        const meta = document.createElement('meta')
        meta.name = 'keywords'
        meta.content = keywords
        document.head.appendChild(meta)
      }
    }

    // Update Open Graph title
    const ogTitleMeta = document.querySelector('meta[property="og:title"]')
    if (ogTitleMeta) {
      ogTitleMeta.setAttribute('content', ogTitle || title)
    } else {
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'og:title')
      meta.content = ogTitle || title
      document.head.appendChild(meta)
    }

    // Update Open Graph description
    const ogDescMeta = document.querySelector('meta[property="og:description"]')
    if (ogDescMeta) {
      ogDescMeta.setAttribute('content', ogDescription || description)
    } else {
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'og:description')
      meta.content = ogDescription || description
      document.head.appendChild(meta)
    }

    // Update Open Graph image
    if (ogImage) {
      const ogImageMeta = document.querySelector('meta[property="og:image"]')
      if (ogImageMeta) {
        ogImageMeta.setAttribute('content', ogImage)
      } else {
        const meta = document.createElement('meta')
        meta.setAttribute('property', 'og:image')
        meta.content = ogImage
        document.head.appendChild(meta)
      }
    }

    // Add canonical URL
    if (canonical) {
      const existingCanonical = document.querySelector('link[rel="canonical"]')
      if (existingCanonical) {
        existingCanonical.setAttribute('href', canonical)
      } else {
        const link = document.createElement('link')
        link.rel = 'canonical'
        link.href = canonical
        document.head.appendChild(link)
      }
    }

    // Add Twitter Card metadata
    const twitterCard = document.querySelector('meta[name="twitter:card"]')
    if (!twitterCard) {
      const meta = document.createElement('meta')
      meta.name = 'twitter:card'
      meta.content = 'summary_large_image'
      document.head.appendChild(meta)
    }

    const twitterTitle = document.querySelector('meta[name="twitter:title"]')
    if (twitterTitle) {
      twitterTitle.setAttribute('content', ogTitle || title)
    } else {
      const meta = document.createElement('meta')
      meta.name = 'twitter:title'
      meta.content = ogTitle || title
      document.head.appendChild(meta)
    }

    const twitterDesc = document.querySelector('meta[name="twitter:description"]')
    if (twitterDesc) {
      twitterDesc.setAttribute('content', ogDescription || description)
    } else {
      const meta = document.createElement('meta')
      meta.name = 'twitter:description'
      meta.content = ogDescription || description
      document.head.appendChild(meta)
    }

    if (ogImage) {
      const twitterImage = document.querySelector('meta[name="twitter:image"]')
      if (twitterImage) {
        twitterImage.setAttribute('content', ogImage)
      } else {
        const meta = document.createElement('meta')
        meta.name = 'twitter:image'
        meta.content = ogImage
        document.head.appendChild(meta)
      }
    }
  }, [title, description, keywords, ogTitle, ogDescription, ogImage, canonical])

  return null // This component only manipulates the head, doesn't render anything
}

export default SEOHead